import { Box } from "@chakra-ui/react"
import { EDITOR_MODE } from "@pathwright/blocks-engine"
import Removable from "@pathwright/blocks/src/shared/Removable"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import classnames from "classnames"
import { useEffect, useRef, useState } from "react"
import CardFlipper from "./CardFlipper"
import FlashcardProvider, { useFlashcard } from "./FlashcardProvider"
import FlashcardSide from "./FlashcardSide"
import { FlashcardButton } from "./ui"

const FlashcardContent = ({
  sideName,
  className,
  onFlip,
  onChangeAudio,
  data,
  cardIndex,
  ...props
}) => {
  const { isFlipped, blockID } = useFlashcard()
  const flipButtonRef = useRef(null)
  const [flipTriggered, setFlipTriggered] = useState(false)

  // Term === front, Definition === back
  const isHidden = isFlipped ? sideName === "term" : sideName === "definition"

  const flipperStyles =
    props.mode === EDITOR_MODE.VIEW
      ? {
          backgroundColor: "transparent !important",
          borderRadius: "0 !important",
          border: "none",
          height: "100%",
          width: "100%",
          right: 0,
          bottom: 0
        }
      : {
          right: "0.5rem",
          bottom: "0.5rem"
        }

  // Handle focus explicitly when flip is triggered
  useEffect(() => {
    if (flipTriggered && !isHidden) {
      setTimeout(() => {
        if (flipButtonRef.current) {
          flipButtonRef.current.focus()
        }
        setFlipTriggered(false)
      }, 0)
    }
  }, [isFlipped, flipTriggered])

  // Handle flip action
  const handleFlip = (e) => {
    // e.preventDefault() // Prevent default spacebar behavior
    setFlipTriggered(true) // Trigger focus logic
    onFlip() // Call the provided flip handler
  }

  return (
    <FlashcardSide
      key={`card-${sideName}`}
      cardIndex={cardIndex}
      side={data}
      onChangeAudio={onChangeAudio(sideName)}
      sideName={sideName}
      {...props}
    >
      <Box
        as={FlashcardButton}
        ref={flipButtonRef}
        isFlipButton
        onClick={handleFlip}
        // inert={isHidden ? "true" : undefined}
        aria-hidden={isHidden}
        aria-labelledby={`${blockID}-flashcard-html-${cardIndex}-${sideName}`}
        tabIndex={isHidden ? -1 : 0}
        className={"show-on-hover"}
        onImage={!!data.image}
        pos="absolute"
        {...flipperStyles}
      >
        {props.mode === EDITOR_MODE.EDIT && (
          <Pathicon icon="flip-horizontal" size="1em" />
        )}
      </Box>
    </FlashcardSide>
  )
}

const Flashcard = (props) => {
  const {
    updateContent,
    onRemoveFlashcard,
    flashcard,
    cardIndex,
    DragHandle,
    mode
  } = props

  const { editingText, setEditingText } = useFlashcard()

  // Can be used for removing audio as well
  const handleUpdateAudio =
    (sideName) =>
    (audioMedia = null) => {
      updateContent(sideName, "audio", audioMedia?.url)
    }

  const classNames = classnames({
    Flashcard: true
  })

  const flipClass =
    mode === EDITOR_MODE.VIEW ? "Flashcard__flipper--full" : null

  return (
    <Removable
      onRemove={onRemoveFlashcard}
      style={{ justifyContent: "center" }}
    >
      {({ onRequestRemove }) => {
        const sideProps = {
          updateContent,
          className: flipClass,
          DragHandle,
          onSetEditingText: setEditingText,
          removeFlashcard: onRequestRemove,
          onChangeAudio: handleUpdateAudio,
          cardIndex,
          mode
        }
        return (
          <Box
            className={classNames}
            pos={"relative"}
            w={"250px"}
            bgColor="transparent !important}"
            // Ensures that Quill Editor menus can overlay other Flashcards
            zIndex={editingText ? 2 : null}
            sx={{
              "& > div:first-of-type > div": {
                // Allow Quill popups to be appear outside of Flashcard
                overflow: "visible"
              },
              ".show-on-hover": {
                transition: "opacity 0.4s",
                opacity: "0"
              }
            }}
            _before={{
              content: '""',
              display: "block",
              paddingTop: "100%",
              height: 0,
              backgroundColor: "transparent"
            }}
            _hover={{
              ".show-on-hover, .Blocks__ImageControls": {
                opacity: editingText ? "0" : "1 !important"
              },
              ".FlashcardImageWrapper": {
                pointerEvents: "all"
              }
            }}
            backgroundColor="transparent !important"
            padding="0.7rem"
          >
            <CardFlipper
              renderFront={(flipperProps) => (
                <FlashcardContent
                  sideName="term"
                  data={flashcard.term}
                  {...sideProps}
                  {...flipperProps}
                />
              )}
              renderBack={(flipperProps) => (
                <FlashcardContent
                  sideName="definition"
                  data={flashcard.definition}
                  {...sideProps}
                  {...flipperProps}
                />
              )}
              pos="absolute"
              top="0"
              left="0"
              // p="10px"
              h="100%"
              w="100%"
            />
          </Box>
        )
      }}
    </Removable>
  )
}

const FlashcardContainer = (props) => {
  return (
    <FlashcardProvider {...props}>
      <Flashcard {...props} />
    </FlashcardProvider>
  )
}

export default FlashcardContainer
